/**
 * PvP Roulette rooms slice — snapshot-based state per roomId.
 *
 * Snapshot-based strategy:
 * - Any incoming snapshot (API initial load, Centrifugo RoundUpdated)
 *   is the full source of truth. We do NOT merge partial updates.
 * - Single canonical entry: ingestRoundSnapshot. Version guard inside.
 * - Bootstrap race: API and Centrifugo may deliver nearly simultaneously;
 *   only the snapshot with max version wins.
 */

import { create } from "zustand"
import { useShallow } from "zustand/react/shallow"
import {
  mergeSnapshotWithOptimisticTrickForView,
  reduceRoomState,
  resolveTricksEndsAtMs,
} from "../model/reduceRoomState"
import { deriveViewState } from "../model/deriveViewState"
import { mergeRoomActivityFeed } from "../model/mergeRoomActivityFeed"
import type { RoomSlice, OptimisticPendingBet } from "../model/types"
import type { RoundSnapshot, TrickType } from "@/shared/api/types/pvpRoulette"
import { TRICK_TYPE_FREEZE_WHEEL } from "@/shared/api/types/pvpRoulette"
import type { PixiCommand } from "../model/commands"
import { getPvpRouletteRuntime } from "@/shared/lib/pvp-roulette-runtime/runtime"
import type { SnapshotSource } from "@/shared/lib/realtime"
import { getUserIdFromToken } from "@/shared/api/auth"
import { resolveWheelUserId } from "@/shared/lib/pvpWheelUserIdentity"

export type { SnapshotSource }

export interface IngestRoundSnapshotOptions {
  source?: SnapshotSource
  /** Required for optimistic pending reconciliation. */
  userId?: string
  /** Deterministic override for ctx.now (used by fixture playback). */
  nowMs?: number
  /**
   * Clock-drift offset in ms: server_time − Date.now() from the RoundUpdated envelope.
   * Applied to UI countdown calculations only. Computed by the realtime layer on each push.
   * Not set for API (HTTP) snapshots — defaults to 0.
   */
  clockOffsetMs?: number
}

interface PvpRoomsState {
  rooms: Record<string, RoomSlice>

  /** Canonical entry for applying a round snapshot. Version guard inside. */
  ingestRoundSnapshot: (
    roomId: string,
    snapshot: RoundSnapshot,
    options?: IngestRoundSnapshotOptions
  ) => void
  /** @deprecated Use ingestRoundSnapshot. Kept for backward compat. */
  ingestSnapshot: (roomId: string, snapshot: RoundSnapshot) => void
  /** Like `ingestSnapshot`, but allows deterministic `ctx.now` override. */
  ingestSnapshotWithNow: (
    roomId: string,
    snapshot: RoundSnapshot,
    options?: IngestRoundSnapshotOptions
  ) => void
  /** Reset room to initial state (for fixture replay). */
  resetRoom: (roomId: string) => void
  placeBetSolo: (
    roomId: string,
    payload: {
      amount: number
      currency: "ton" | "stars"
      sectorId?: string
      correlationId?: string
    }
  ) => void
  clearOptimisticPending: (roomId: string) => void
  useTrickSolo: (roomId: string) => void
  applyTrickOptimistic: (
    roomId: string,
    trickType: TrickType,
    userId?: string
  ) => boolean
  ackCommands: (roomId: string, upToId: string) => void
}

function initialSlice(): RoomSlice {
  return {
    rawRound: null,
    viewState: null,
    commandsQueue: [],
    optimisticPending: null,
    optimisticTrickPending: null,
    lastAcceptedVersion: 0,
    activityFeed: [],
    clockOffsetMs: undefined,
  }
}

export const usePvpRoomsStore = create<PvpRoomsState>((set, get) => ({
  rooms: {},

  ingestRoundSnapshot(
    roomId: string,
    snapshot: RoundSnapshot,
    options?: IngestRoundSnapshotOptions
  ) {
    set((state) => {
      const slice = state.rooms[roomId] ?? initialSlice()
      const result = reduceRoomState(slice, snapshot, {
        now: options?.nowMs ?? Date.now(),
        userId:
          options?.userId ??
          resolveWheelUserId(snapshot) ??
          getUserIdFromToken() ??
          undefined,
        clockOffsetMs: options?.clockOffsetMs,
      })
      const nextSlice =
        result.slice === slice
          ? slice
          : mergeRoomActivityFeed(slice, result.slice)
      return {
        rooms: {
          ...state.rooms,
          [roomId]: nextSlice,
        },
      }
    })
  },

  ingestSnapshot(roomId: string, snapshot: RoundSnapshot) {
    get().ingestRoundSnapshot(roomId, snapshot, {})
  },
  ingestSnapshotWithNow(
    roomId: string,
    snapshot: RoundSnapshot,
    options?: IngestRoundSnapshotOptions
  ) {
    get().ingestRoundSnapshot(roomId, snapshot, options ?? {})
  },

  resetRoom(roomId: string) {
    set((state) => ({
      rooms: {
        ...state.rooms,
        [roomId]: initialSlice(),
      },
    }))
  },

  placeBetSolo(
    roomId: string,
    payload: {
      amount: number
      currency: "ton" | "stars"
      sectorId?: string
      correlationId?: string
    }
  ) {
    set((state) => {
      const slice = state.rooms[roomId] ?? initialSlice()
      const viewState = slice.viewState
      if (!viewState?.actionsAllowed.canPlaceBet) return state

      const now = Date.now()
      const pending: OptimisticPendingBet = {
        correlationId: payload.correlationId ?? `pending_${now}`,
        roomId,
        amount: payload.amount,
        currency: payload.currency,
        sectorId: payload.sectorId,
        submittedAt: now,
        ttlMs: getPvpRouletteRuntime().PENDING_TTL_MS,
      }

      return {
        rooms: {
          ...state.rooms,
          [roomId]: {
            ...slice,
            optimisticPending: pending,
          },
        },
      }
    })
  },

  clearOptimisticPending(roomId: string) {
    set((state) => {
      const slice = state.rooms[roomId]
      if (!slice?.optimisticPending) return state
      return {
        rooms: {
          ...state.rooms,
          [roomId]: { ...slice, optimisticPending: null },
        },
      }
    })
  },

  useTrickSolo(roomId: string) {
    set((state) => {
      const slice = state.rooms[roomId] ?? initialSlice()
      const viewState = slice.viewState
      if (!viewState?.actionsAllowed.canUseTrick) return state
      // Trick validation done here. Actual trick triggers via API mutation.
      return state
    })
  },

  applyTrickOptimistic(roomId: string, trickType: TrickType, userId?: string) {
    let applied = false
    set((state) => {
      const slice = state.rooms[roomId]
      if (!slice?.rawRound) return state

      const uid = userId ?? getUserIdFromToken()
      if (!uid) return state

      const now = Date.now()
      const snapshotForTrickGate = mergeSnapshotWithOptimisticTrickForView(
        slice.rawRound,
        slice.optimisticTrickPending ?? null
      )
      const vs = deriveViewState(snapshotForTrickGate, {
        now,
        userId: uid,
        clockOffsetMs: slice.clockOffsetMs,
      })
      if (!(vs.actionsAllowed.canApplyTrickByType[trickType] ?? false))
        return state

      const alreadyApplied = slice.rawRound.tricks.some(
        (t) => t.userId === uid && t.type === trickType
      )
      if (alreadyApplied) return state

      const nextRawRound: RoundSnapshot = {
        ...slice.rawRound,
        tricks: [...slice.rawRound.tricks, { userId: uid, type: trickType }],
      }

      const tricksEndMs = resolveTricksEndsAtMs(nextRawRound)
      const trickEffectKey = `${uid}:${trickType}`
      const optimisticCommand: PixiCommand | null =
        tricksEndMs != null
          ? {
              id: `optimistic:${now}:${trickType}`,
              roomId: nextRawRound.roundId,
              roundVersion: nextRawRound.version,
              type:
                trickType === TRICK_TYPE_FREEZE_WHEEL
                  ? "ApplyTrickFreeze"
                  : "ApplyTrickPush",
              payload: { tricksEndsAt: tricksEndMs, trickEffectKey },
            }
          : null

      applied = true
      return {
        rooms: {
          ...state.rooms,
          [roomId]: {
            ...slice,
            rawRound: nextRawRound,
            optimisticTrickPending: {
              roundId: nextRawRound.roundId,
              userId: uid,
              trickType,
              submittedAt: now,
            },
            viewState: deriveViewState(nextRawRound, {
              now,
              userId: uid,
              clockOffsetMs: slice.clockOffsetMs,
            }),
            commandsQueue: optimisticCommand
              ? [...slice.commandsQueue, optimisticCommand]
              : slice.commandsQueue,
          },
        },
      }
    })
    return applied
  },

  ackCommands(roomId: string, upToId: string) {
    set((state) => {
      const slice = state.rooms[roomId]
      if (!slice) return state
      const idx = slice.commandsQueue.findIndex((c) => c.id === upToId)
      if (idx < 0) return state
      const commandsQueue = slice.commandsQueue.slice(idx + 1)
      return {
        rooms: {
          ...state.rooms,
          [roomId]: { ...slice, commandsQueue },
        },
      }
    })
  },
}))

// ─────────────────────────────────────────────────────────────────────────────
// Granular selectors (shallow)
// ─────────────────────────────────────────────────────────────────────────────

export function usePvpRoomSlice(roomId: string | null): RoomSlice | null {
  return usePvpRoomsStore(
    useShallow((s) => (roomId ? (s.rooms[roomId] ?? null) : null))
  )
}

export function usePvpRoomViewState(roomId: string | null) {
  return usePvpRoomsStore(
    useShallow((s) => (roomId ? (s.rooms[roomId]?.viewState ?? null) : null))
  )
}

export function usePvpRoomCommands(roomId: string | null): PixiCommand[] {
  return usePvpRoomsStore(
    useShallow((s) => (roomId ? (s.rooms[roomId]?.commandsQueue ?? []) : []))
  )
}

export function usePvpRoomOptimisticPending(
  roomId: string | null
): OptimisticPendingBet | null {
  return usePvpRoomsStore(
    useShallow((s) =>
      roomId ? (s.rooms[roomId]?.optimisticPending ?? null) : null
    )
  )
}
